import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { MosaicComoLinkAccountCardBlockConfig } from '@front-platform/content-builder';
import { mosaicComoLinkAccountCardBlockResolveConfig } from '@front-platform/content-builder';
import { of } from 'rxjs';
import { Component, OnInit, input } from '@angular/core';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';

import { ComoUser } from '@core/models/como.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { ComoService } from '@core/services/como/como.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';

import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import { ComoLinkAccountModalComponent } from '@shared/modals/como/como-link-account-modal/como-link-account-modal.component';
export interface MosaicComoLinkAccountCardBlockRuntimeConfig {}

export type MosaicComoLinkAccountCardBlockConfigInput = Partial<
  Omit<MosaicComoLinkAccountCardBlockConfig, keyof MosaicComoLinkAccountCardBlockRuntimeConfig> &
    MosaicComoLinkAccountCardBlockRuntimeConfig
>;
export type MosaicComoLinkAccountCardBlockResolvedConfig = Omit<
  MosaicComoLinkAccountCardBlockConfig,
  keyof MosaicComoLinkAccountCardBlockRuntimeConfig
> &
  Partial<MosaicComoLinkAccountCardBlockRuntimeConfig>;
export interface MosaicComoLinkAccountCardBlockContract extends ConfigComponentContract<
  MosaicComoLinkAccountCardBlockConfigInput,
  MosaicComoLinkAccountCardBlockResolvedConfig
> {}

@Component({
  selector: 'app-mosaic-como-link-account-card-block',
  templateUrl: './mosaic-como-link-account-card-block.component.html',
  styleUrls: ['./mosaic-como-link-account-card-block.component.scss'],
  standalone: false,
})
export class MosaicComoLinkAccountCardBlockComponent
  extends DestroyerBase
  implements OnInit, MosaicComoLinkAccountCardBlockContract
{
  readonly config = input<MosaicComoLinkAccountCardBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    MosaicComoLinkAccountCardBlockConfig,
    MosaicComoLinkAccountCardBlockRuntimeConfig
  >(mosaicComoLinkAccountCardBlockResolveConfig, () => ({ config: this.config() }));
  get heading(): string | undefined {
    return this.resolvedConfig()['heading'] as unknown as string | undefined;
  }
  get text(): string | undefined {
    return this.resolvedConfig()['text'] as unknown as string | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get textColor(): string | undefined {
    return this.resolvedConfig()['text_color'] as unknown as string | undefined;
  }

  isLoading = true;
  comoUser: ComoUser | null = null;

  isComoEnabled$ = this.marketplaceRefiner.selectIsComoPluginEnabled;

  constructor(
    private readonly comoService: ComoService,
    private cartDispatcher: CartStoreDispatcher,
    private readonly modalsService: ModalsService,
    readonly authFirebaseService: AuthFirebaseService,
    private marketplaceRefiner: MarketplaceStoreRefiner,
  ) {
    super();
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.isComoEnabled$
      .pipe(
        takeUntil(this._destroyerRef),
        switchMap(isComoEnabled => {
          if (!isComoEnabled) {
            this.isLoading = false;
            return of(false);
          }
          this.comoService.loadComoUser();
          return this.comoService.getComoUser$().pipe(
            takeUntil(this._destroyerRef),
            tap(comoUser => (this.comoUser = comoUser ?? null)),
            tap(() => (this.isLoading = false)),
          );
        }),
      )
      .subscribe();
  }

  onClick(): void {
    this.authFirebaseService
      .isAuthenticate$()
      .pipe(take(1))
      .subscribe(isAuthenticated => {
        if (!isAuthenticated || this.authFirebaseService.isAnonymous) {
          this.cartDispatcher.resetActive();
          this.modalsService.open(AuthenticationModalComponent.handle);
        } else {
          this.modalsService.open(ComoLinkAccountModalComponent.handle);
        }
      });
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(MosaicComoLinkAccountCardBlockComponent, {
  'keyAliases': {},
});
